import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Expand a DOM Range to whole word spans and return the reconstructed
 * plain-text selection.
 *
 * Why this exists
 * ----------------
 * The Slate editor renders each word as an inline `<span class="word"
 * data-word-index="...">...</span>`. When the user drags a selection across
 * several of those spans, `window.getSelection().toString()` (and
 * `range.toString()`) return only the *interior* of the first and last span,
 * clipping the leading and trailing characters. A selection that visually
 * covers "Much. Your Excellency, Madam President" becomes the string
 * "uch. Your Excellency, Madam Presid" — which of course never matches
 * `block.text` and Replace All reports "0 occurrences".
 *
 * Fix: iterate over every `.word[data-word-index]` span inside the editor
 * container, collect the ones whose DOM position intersects the range,
 * and join their full `textContent` with single spaces. That output is
 * byte-for-byte identical to how `blockToSlateValue` laid out the words in
 * `block.text`, so the word-boundary regex matches reliably.
 *
 * @returns {string|null} Reconstructed text, or null if we can't find any
 *   word spans in the range (falls back to raw selection in that case).
 */
const extractWholeWordsFromRange = (range, container) => {
  if (!range || !container || range.collapsed) return null;

  let wordSpans;
  try {
    wordSpans = container.querySelectorAll('.word[data-word-index]');
  } catch (_e) {
    return null;
  }
  if (!wordSpans || wordSpans.length === 0) return null;

  const selected = [];
  for (const span of wordSpans) {
    try {
      if (range.intersectsNode(span)) {
        selected.push(span);
      }
    } catch (_e) {
      // Detached / disconnected nodes can throw; skip them silently.
    }
  }

  if (selected.length === 0) return null;

  return selected
    .map((s) => (s.textContent || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' ');
};

/**
 * useTextSelection Hook
 * 
 * Detects text selection within transcript blocks and validates whole word selection
 */
const useTextSelection = (containerRef) => {
  const [selection, setSelection] = useState(null);
  const selectionTimeoutRef = useRef(null);

  /**
   * Get selection position (kept for potential future use)
   */
  const getSelectionPosition = useCallback((range) => {
    const rects = range.getBoundingClientRect();
    const containerRect = containerRef?.current?.getBoundingClientRect() || { left: 0, top: 0 };

    return {
      top: rects.top - containerRect.top - 60, // Position toolbar above selection
      left: rects.left - containerRect.left + (rects.width / 2), // Center horizontally
      width: rects.width,
    };
  }, [containerRef]);

  /**
   * Handle selection change
   */
  const handleSelectionChange = useCallback(() => {
    // Clear existing timeout
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    // Debounce selection detection
    selectionTimeoutRef.current = setTimeout(() => {
      const windowSelection = window.getSelection();
      
      if (!windowSelection || windowSelection.rangeCount === 0) {
        setSelection(null);
        return;
      }

      const range = windowSelection.getRangeAt(0);
      const rawText = windowSelection.toString();

      // Ignore empty selections or collapsed selections (just cursor)
      if (!rawText.trim() || range.collapsed) {
        setSelection(null);
        return;
      }

      // Check if selection is within our container
      if (containerRef?.current && !containerRef.current.contains(range.commonAncestorContainer)) {
        setSelection(null);
        return;
      }

      // Slate renders every word as an inline <span class="word" data-word-
      // index="..."> element. `window.getSelection().toString()` clips the
      // first/last character of boundary spans, which breaks Replace All.
      // Expand to whole word spans to get a clean, block.text-aligned string.
      const expanded = extractWholeWordsFromRange(range, containerRef?.current);
      const selectedText = (expanded || rawText).trim();

      setSelection({
        text: selectedText,
        isValid: true,
        position: getSelectionPosition(range),
        range: range,
      });
    }, 100); // 100ms debounce
  }, [containerRef, getSelectionPosition]);

  /**
   * Clear selection
   */
  const clearSelection = useCallback(() => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  /**
   * Setup event listeners
   */
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleSelectionChange);
      
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [handleSelectionChange]);

  return {
    selection,
    clearSelection,
  };
};

export default useTextSelection;

